export interface CustomLogoSetup {
  logoUrl: string;
  darkModeLogoUrl: string;
  colorHex: string;
}

export interface IMS {
  type: string;
  customType?: string;
  protocol?: string;
  customProtocol?: string;
  im: string;
  primary: boolean;
}

export interface Email {
  address: string;
  type?: string;
  customType?: string | null;
  primary: boolean;
}

export interface ExternalId {
  value: string;
  type: string;
  customType?: string;
}

export interface Relation {
  value: string;
  type: string;
  customType?: string;
}

export interface Address {
  type: string;
  customType?: string;
  sourceIsStructured: boolean;
  formatted: string;
  poBox: string;
  extendedAddress: string;
  streetAddress: string;
  locality: string;
  region: string;
  postalCode: string;
  country: string;
  primary: boolean;
  countryCode?: string;
}

export interface PersonAddress {
  formattedValue: string;
  poBox: string;
  streetAddress: string;
  extendedAddress: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  countryCode: string;
  type: string;
}

export interface Organization {
  name: string;
  title: string | null;
  primary: boolean;
  type?: string | null;
  customType?: string;
  department: string | null;
  symbol: string;
  location: string;
  description: string;
  domain: string;
  costCenter: string;
  metadata?: MetaData;
}

export interface Phone {
  value: string;
  type: string;
  customType?: string;
  primary: boolean;
}

export interface Website {
  url: string;
  type: string;
  customType?: string;
  primary: boolean;
}

export interface Name {
  familyName: string;
  givenName: string;
  fullName: string;
}

export interface Location {
  area: string;
  buildingId: string;
  deskCode: string;
  floorName: string;
  floorSection: string;
  type: string;
}

export interface Gender {
  addressMeAs: string;
  type: 'male' | 'female' | 'other' | 'unknown' | '';
  customType?: string;
}

export interface SchemaValue {
  fieldDisplayName: string;
  value?: string;
  values: string[];
}

export interface CustomSchema {
  values: SchemaValue[];
  schemaName: string;
}

export interface User {
  lastLoginTime: number | null;
  creationTime: number;
  deletionTime: number | null;
  agreedToTerms: boolean;
  suspended: boolean;
  changePasswordAtNextLogin: boolean;
  ipWhitelist: boolean;
  includeInGlobalAddressList: boolean;
  password: string | null;
  hashFunction: string | null;
  suspendedReason: string | null;
  customerId: string;
  orgUnitPath: string;
  thumbnailPhotoUrl: string | null;
  id: string;
  primaryEmail: string;
  notes: string | null;
  ims: IMS[] | null;
  emails: Email[] | null;
  externalIds: ExternalId[] | null;
  relations: Relation[] | null;
  addresses: Address[] | null;
  organizations: Organization[] | null;
  phones: Phone[] | null;
  websites: Website[] | null;
  aliases: string[] | null;
  nonEditableAliases: string[] | null;
  name: Name | null;
  locations: Location[] | null;
  gender: Gender | null;
  isArchived: boolean;
  accountCreatedTime: number;
  emailUsage: number;
  driveUsage: number;
  totalStorage: number;
  isEnforcedIn2Sv: boolean;
  isEnrolledIn2Sv: boolean;
  isAdmin: boolean;
  customSchemas: CustomSchema[] | null;
  currentSchemaName: string | null;
  recoveryEmail: string | null;
  recoveryPhone: string | null;
}

export interface DestUser {
  userEmail: string;
  role: 'reader' | 'commenter' | 'writer' | 'owner';
  type: 'user' | 'group';
}

export const VAULT_CORPUS_VALUES = [
  'MAIL',
  'DRIVE',
  'GROUPS',
  'HANGOUTS_CHAT',
  'VOICE',
  'CALENDAR',
  'GEMINI',
  'UNKNOWN',
] as const;

export type VaultCorpus = (typeof VAULT_CORPUS_VALUES)[number];

export enum VaultDataScope {
  ALL_DATA = 'ALL_DATA',
  HELD_DATA = 'HELD_DATA',
  UNPROCESSED_DATA = 'UNPROCESSED_DATA',
  UNKNOWN = 'UNKNOWN',
}

export const VAULT_DATA_SCOPE_OPTIONS = [
  { value: VaultDataScope.ALL_DATA, label: 'All Data' },
  { value: VaultDataScope.HELD_DATA, label: 'Held Data' },
  { value: VaultDataScope.UNPROCESSED_DATA, label: 'Unprocessed Data' },
] as const;

export const VAULT_MATTER_REGION_OPTIONS = [
  { value: 'MATTER_REGION_UNSPECIFIED', label: 'Unspecified' },
  { value: 'ANY', label: 'Any region' },
  { value: 'US', label: 'United States region' },
  { value: 'EUROPE', label: 'Europe region' },
] as const;

export const VAULT_EXPORT_REGION_OPTIONS = [
  { value: 'EXPORT_REGION_UNSPECIFIED', label: 'Unspecified' },
  { value: 'ANY', label: 'Any region' },
  { value: 'US', label: 'United States region' },
  { value: 'EUROPE', label: 'Europe region' },
] as const;

export enum AttendeeResponse {
  UNSPECIFIED = 'ATTENDEE_RESPONSE_UNSPECIFIED',
  NEEDS_ACTION = 'ATTENDEE_RESPONSE_NEEDS_ACTION',
  ACCEPTED = 'ATTENDEE_RESPONSE_ACCEPTED',
  DECLINED = 'ATTENDEE_RESPONSE_DECLINED',
  TENTATIVE = 'ATTENDEE_RESPONSE_TENTATIVE',
}

export interface UserListEntity {
  firstName: string;
  lastName: string;
  userEmail: string;
  suspended: boolean;
  archived: boolean;
  orgUnitPath: string;
  accountCreatedTime: number;
  lastUpdateTime: number;
  domain: string | null;
  jobTitle?: string;
  userId?: string;
  orgUnitName?: string;
  aliases?: string[];
  groupEmails?: string[];
}

export interface GPanelRole {
  roleName: string;
  desc: string;
  roleType: string;
  createdDate: number;
  lastUpdated: number;
  lastUpdatingUser: string | null;
}

export interface DomainInfo {
  primaryDomain: string;
  adminEmail: string | null;
  subDomains: string[];
  allDomains: string[];
}

interface Role_Client {
  roleName: string;
  default: boolean;
  desc: string;
  roleType: 'user' | 'admin';
  createdDate: number;
  lastUpdated: number;
  lastUpdatingUser: string | null;
  userRoles: string[] | null;
  directoryRoles: string[] | null;
  gPanelContactsRoles: string[] | null;
  calendarRoles: string[] | null;
  driveRoles: string[] | null;
  sitesRoles: string[] | null;
  auditComplianceRoles: string[] | null;
  reportingRoles: string[] | null;
  riskManagementRoles: string[] | null;
  discoveryRoles: string[] | null;
  toolsRoles: string[] | null;
  administrationRoles: string[] | null;
  rulesEngineRoles: string[] | null;
  userViewRoles: string[] | null;
  userDirectorySearchRoles: string[] | null;
  immediateNotification: string[] | null;
  dailyDigest: string[] | null;
  notificationUsers: string[] | null;
  changeLogs: string[] | null;
  changeLogLength: number | null;
  limitedVisEnabled: boolean;
  limitedVisAllowList: string[] | null;
  limitedVisBlockList: string[] | null;
  limitedVisOrgList: string[] | null;
  limitedVisRoleList: string[] | null;
  limitedVisDomainList: string[] | null;
  dirsearchBlockListOrgs: string[] | null;
  dirsearchBlockListGroups: string[] | null;
  canSeePasswords: boolean;
  billingRoles: string[] | null;
  chromeManagementRoles: string[] | null;
}

export type gPanel_Tier =
  | 'essentials'
  | 'beyond'
  | 'core'
  | 'enterprise'
  | 'standard'
  | 'starter'
  | 'trial';

export interface UserTier {
  value: gPanel_Tier;
  displayValue: string;
  rank: number;
}

export interface MarketingOverviewModel {
  id: number;
  dateCreated: number;
  url: string;
  title: string;
  barColor: string;
  textColor: string;
  buttonColor: string;
  buttonTextColor: string;
  buttonText: string;
  active: boolean;
  openInNewTab: boolean;
  barType: 'Test' | 'Live' | 'Permanent';
  hasButton: boolean;
}

interface TrustedTesterModel {
  domain: string;
  createdDate: number;
  creator: string;
  featureAccess: string[];
}

interface TrustedTesterModel {
  domain: string;
  createdDate: number;
  creator: string;
  featureAccess: string[];
}

interface SubscriptionModel {
  createdDate: number;
  timeout: number;
  emailDomain: string;
  commitType: 'trial' | 'monthly' | 'yearly' | 'flexible';
  billingType:
    | 'annualPrepay'
    | 'annualOnBillDate'
    | 'monthlyPrepay'
    | 'monthlyPostPay'
    | 'monthlyOnBillDate'
    | 'semiAnnual';
  adminEmail: string;
  contactEmail: string;
  zohoParentId: string;
  zohoLeadId: string;
  active: boolean;
  userCount: number;
  keyString: string;
  renewalId: number;
  updatedLead: boolean;
  subscriptionStatus:
    | 'active'
    | 'inRenewal'
    | 'cancelled'
    | 'nonRenewing'
    | 'inactive'
    | 'trialExpired'
    | 'allActive';
  startDate: number;
  subscriptionCreationReason:
    | 'newTrial'
    | 'netNew'
    | 'upgrade'
    | 'renewal'
    | 'incrementLicense'
    | 'licenseChange'
    | 'gabNetNew'
    | 'gabNetNewTransfer'
    | 'extendedTrial';
  domainEdition: 'Business' | 'Government' | 'Education' | 'Nonprofit';
  billDate: number;
  indexId: string;
  infsId: number;
}

export interface GpanelSubscriptionModel extends SubscriptionModel {
  gabCustomer: boolean;
  purchasedLicenses: number;
  gPanelPackage: gPanel_Tier;
  timezone: number;
  customerId: string;
  gPanelUserCount: number;
  gPanelRenewalType: 'AUTO_RENEW' | 'CANCEL' | 'RENEW_CURRENT_USERS' | 'REDUCE';
  version: 'v1' | 'v2';
  vfeLicenseCount: number;
  offline: boolean;
  licenseBuffer: number;
  isGcpMarketplace: boolean;
  qbInstance: 'us' | 'canada';
}

interface SavedGridState {
  name: string;
  createdDate: number;
  color: string;
  value: string;
}

export interface MuiGridState {
  activeGridState: 'default' | string | null;
  savedGridStates: SavedGridState[];
}

export interface ViewType {
  value: 'RichContent' | 'Compact';
  umLayout: 'grid' | 'pickList';
  orgUnitLayout: 'grid' | 'pickList';
  expandedNav: boolean;
  buttonMode: 'ICON' | 'BUTTON';
  isReactView: boolean;
  colorMode: 'light' | 'dark';
  bookmarks: string[];
  reportsGridStates: MuiGridState | null;
  policiesGridStates: MuiGridState | null;
  contactSyncsGridStates: MuiGridState | null;
  bulkOpsGridStates: MuiGridState | null;
  gmailDeletionsGridStates: MuiGridState | null;
  gmailExportsGridStates: MuiGridState | null;
  apisGridStates: MuiGridState | null;
  driveSweepsGridStates: MuiGridState | null;
  signatureTemplatesGridStates: MuiGridState | null;
  rulesEnginesGridStates: MuiGridState | null;
  driveSearchesGridStates: MuiGridState | null;
  calendarTransferLogsGridStates: MuiGridState | null;
  ownershipTransferLogsGridStates: MuiGridState | null;
  groupTemplatesGridStates: MuiGridState | null;
  calendarsGridStates: MuiGridState | null;
  resourceCalendarsGridStates: MuiGridState | null;
  buildingsGridStates: MuiGridState | null;
  resourceTemplatesGridStates: MuiGridState | null;
  userManagementIndexedGridStates: MuiGridState | null;
  userManagementGridStates: MuiGridState | null;
  labelManagerGridStates: MuiGridState | null;
  groupsIndexedGridStates: MuiGridState | null;
  groupsGridStates: MuiGridState | null;
  chromeDevicesGridStates: MuiGridState | null;
  mobileManagementGridStates: MuiGridState | null;
  mobileManagementIndexedGridStates: MuiGridState | null;
  sharedDrivesGridStates: MuiGridState | null;
  sharedDrivesIndexedGridStates: MuiGridState | null;
  orgUnitsGridStates: MuiGridState | null;
  orgUnitsIndexedGridStates: MuiGridState | null;
  loggingGridStates: MuiGridState | null;
  cpanelLogsGridStates: MuiGridState | null;
  sharedContactsGridStates: MuiGridState | null;
  gpanelContactsGridStates: MuiGridState | null;
  trashedContactLabelsGridStates: MuiGridState | null;
  trashedContactsGridStates: MuiGridState | null;
  decomPoliciesGridStates: MuiGridState | null;
  decomSuspendedUsersGridStates: MuiGridState | null;
  decomLogsGridStates: MuiGridState | null;
  vaultMattersGridStates: MuiGridState | null;
  vaultExportsGridStates: MuiGridState | null;
  slackChannelsGridStates: MuiGridState | null;
  userRolesGridStates: MuiGridState | null;
}

// Type for valid grid state keys that have MuiGridState type
export type GridStateKey = keyof {
  [K in keyof ViewType as ViewType[K] extends MuiGridState | null
    ? K
    : never]: ViewType[K];
};

export interface UserSettings {
  loggedInUser: string;
  firebaseDbPath: string;
  userName: string;
  idToken: string;
  role: Role_Client | null;
  domainInfo: DomainInfo | null;
  roleAdmin: string;
  currentTier: gPanel_Tier | null;
  marketingOverview: MarketingOverviewModel | null;
  trustedTester: TrustedTesterModel | null;
  subscription: GpanelSubscriptionModel | null;
  viewType: ViewType | null;
  licenseLockoutDate: number | null;
  isOverLicenses: boolean;
  isUnderLicenseLockout: boolean;
  isPlatform: boolean;
  environment: string;
  apiUrl: string;
  featureFlags: object | null;
  features: Record<string, boolean> | null;
  lastAccessTime: number;
  sessionTimeout: number;
  isFirstGpanelLogin: boolean;
  hasRoleWithLimitedVisibility: boolean;
}

export interface UserSearch {
  firstName: string;
  lastName: string;
  userEmail: string;
  orgUnitPath: string;
  role: string;
  domain: string;
  properCaseEmail: string;
}

export interface GroupSearch {
  email: string;
  name: string;
  directMemberCount: number;
  domain: string;
}

export interface AliasSearch {
  firstName: string;
  lastName: string;
  aliasEmail: string;
  primaryEmail: string;
}

export interface GroupAliasSearch {
  email: string;
  name: string;
  aliasEmail: string;
}

export interface ReactSearchResult {
  users: UserSearch[];
  groups: GroupSearch[];
  aliases: AliasSearch[];
  groupAliases: GroupAliasSearch[];
}

export interface FirebaseUserModel {
  keyName?: string;
  firebaseUserId: string;
  firebaseDbPath: string;
  userEmail: string;
  lastKeepAliveMessageSent: number;
  alive: number;
  primaryDomain: string;
}

// from SearchService
export interface OrgUnit {
  displayString: string;
  fieldsToReturn: string[];
  lastUpdatedTime: number;
  name: string;
  orgUnitId: string;
  orgUnitPath: string;
  parentOrgUnitId: string | null;
  parentOrgUnitPath: string | null;
  rowHtml: string;
  searchableEntities: string[];
  children?: OrgUnit[];
}

export interface UserSearchEntity {
  displayString: string;
  domain: string;
  fieldsToReturn: string[];
  firstName: string;
  lastName: string;
  orgUnitPath: string;
  properCaseEmail: string;
  role: string;
  rowHtml: string;
  searchableEntities: string[];
  userEmail: string;
}

// from OrganizationalUnitService
export interface OrgUnitModel {
  blockInheritance: boolean;
  name: string;
  orgUnitId: string;
  orgUnitPath: string;
  description: string;
  parentOrgUnitId: string;
  parentOrgUnitPath: string;
  children?: OrgUnitModel[];
}

export interface Group {
  email: string;
  name: string;
  directMemberCount: number;
  domain: string;
  id?: string;
  groupEmail?: string;
  description?: string;
  adminCreated?: boolean;
  aliases?: string[];
  etag?: string;
  kind?: string;
  nonEditableAliases?: string[];
  directMembersCount?: number;
  // Only present when the groupsGovernanceBackend feature flag is enabled for the domain — absent,
  // not false, when it isn't, so the grid can hide these columns rather than show a misleading
  // value. Requires the backend's GroupMembers index (see Feature.indexGroupMembers) to have been
  // backfilled, or the join will return empty data even with the flag on.
  hasOwner?: boolean;
  hasManager?: boolean;
  // Comma-separated tags (e.g. "ownerless,inactive") when a group matches more than one
  // condition, or the bare literal "healthy" when it matches none.
  health?: string;
}

export interface GroupHealthSummary {
  total: number;
  ownerless: number;
  managerless: number;
  inactive: number;
  healthy: number;
}

export interface GroupHealthSettings {
  flagOwnerless: boolean;
  flagManagerless: boolean;
  flagInactive: boolean;
  inactivityThresholdDays: number;
}

export interface Event {
  date: CalendarDate;
  type: string;
}

export interface ImClient {
  username: string;
  protocol: string;
  formattedProtocol: string;
  type: string;
}

export interface EmailAddress {
  value: string;
  displayName: string;
  type: string;
}

export interface ContactName {
  displayName?: string;
  displayNameLastFirst?: string;
  unstructuredName?: string;
  familyName?: string | null;
  givenName?: string | null;
  middleName?: string;
  honorificPrefix?: string;
  honorificSuffix?: string;
  phoneticFullName?: string;
  honeticFamilyName?: string;
  phoneticGivenName?: string;
  phoneticMiddleName?: string;
  phoneticHonorificPrefix?: string;
  phoneticHonorificSuffix?: string;
  metaData?: MetaData;
}

export interface PhoneNumber {
  value: string;
  type: string;
  canonicalForm: string;
  metadata: MetaData;
}

export interface MetaData {
  primary: boolean;
}

export interface contactGroupMembership {
  contactGroupId: string;
  contactGroupResourceName: string;
}

export interface Membership {
  contactGroupMembership: contactGroupMembership;
}

export interface Url {
  value: string;
  type: string;
}

export interface Birthday {
  metaData?: MetaData;
  date: CalendarDate | null;
  text: string;
}

export interface CalendarDate {
  day: number;
  month: number;
  year: number;
}

export interface NickName {
  metaData?: MetaData;
  value: string;
  type: string;
}

export interface Biography {
  value: string;
  type?: string | null;
}

export interface UserDefined {
  key: string;
  value: string;
  type: string;
}

export interface ContactRelation {
  person: string;
  type: string;
}

export interface PersonOrg {
  startDate: Date;
  endDate: Date;
  current: boolean;
  name: string;
  phoneticName: string;
  department: string;
  title: string;
  jobDescription: string;
  symbol: string;
  domain: string;
  location: string;
  type: string;
}

export interface PersonMetadata {
  previousResourceNames: string[] | null;
  linkedPeopleResourceName: string[] | null;
  deleted: boolean;
  sources: Source[];
}

export interface Source {
  type: string;
  id: string;
  etag: string;
  updateTime: string;
  profileMetadata: ProfileMetadata | null;
}

export interface ProfileMetadata {
  objectType: string;
  userTypes: string[];
}

export interface Person {
  resourceName: string | null | undefined;
  etag: string;
  addresses: PersonAddress[] | null;
  emailAddresses: EmailAddress[] | null;
  phoneNumbers: PhoneNumber[] | null;
  events: Event[] | null;
  imClients: ImClient[] | null;
  names: ContactName[] | null;
  organizations: PersonOrg[] | null;
  memberships: Membership[];
  urls: Url[] | null;
  birthdays: Birthday[] | null;
  nicknames: NickName[] | null;
  biographies: Biography[] | null;
  userDefined: UserDefined[] | null;
  relations: ContactRelation[] | null;
  metadata?: PersonMetadata;
}

export interface ContactGroup {
  resourceName: string | null | undefined;
  etag: string;
  name: string;
  formattedName: string;
  memberResourceNames: string[];
  memberCount: number;
  groupType:
    | 'GROUP_TYPE_UNSPECIFIED'
    | 'USER_CONTACT_GROUP'
    | 'SYSTEM_CONTACT_GROUP';
  systemGroup?: boolean;
}

export interface SnackbarType {
  open: boolean;
  severity: 'success' | 'error' | 'warning' | 'info';
  message: string;
  action?: { label: string; onClick: () => void };
}

export interface ApiException {
  exceptionType:
    | 'Default'
    | 'RefreshOption'
    | 'SerializationError'
    | 'PotentialReorder'
    | 'Notice'
    | 'ForceRefresh'
    | 'NonBillingOrderCancellation'
    | 'DriveLabelRemovalPartialFailure'
    | 'DriveStorageUnavailable'
    | 'ReusedPassword';
  isShowable?: boolean;
  message: string | null;
  header: string | null;
  isAccessException?: boolean;
  successCount?: number;
  failureCount?: number;
}

export interface UserRole {
  roleName: string;
  desc: string;
  roleType: 'admin' | 'user';
  createdDate: number;
  lastUpdated: number;
  lastUpdatingUser: string | null;
  creatorEmail: string | null;
  // should be enum of all available roles
  userRoles: string[] | null;
  directoryRoles: string[] | null;
  gPanelContactsRoles: string[] | null;
  calendarRoles: string[] | null;
  driveRoles: string[] | null;
  sitesRoles: string[] | null;
  auditComplianceRoles: string[] | null;
  reportingRoles: string[] | null;
  riskManagementRoles: string[] | null;
  discoveryRoles: string[] | null;
  toolsRoles: string[] | null;
  administrationRoles: string[] | null;
  userViewRoles: string[] | null;
  userDirectorySearchRoles: string[] | null;
  immediateNotification: string[] | null;
  dailyDigest: null;
  notificationUsers: string[] | null;
  changeLogs: string[] | null;
  changeLogLength: number | null;
  limitedVisEnabled: boolean;
  limitedVisAllowList: string[] | null;
  limitedVisBlockList: string[] | null;
  limitedVisOrgList: string[] | null;
  limitedVisRoleList: string[] | null;
  limitedVisDomainList: string[] | null;
  limitedGroupVisDomainList: string[] | null;
  dirsearchBlockListOrgs: string[] | null;
  dirsearchBlockListGroups: string[] | null;
  canSeePasswords: boolean;
  billingRoles: string[] | null;
  chromeManagementRoles: string[] | null;
  rulesEngineRoles: string[] | null;
  default: boolean;
}

export interface PermissionItem {
  name: string;
  value: string;
  type: string;
  group: string;
  currentlyUsed: boolean;
  hasChangeLog: boolean;
  isBulkOp?: boolean;
}

export interface RoleConfig {
  [key: string]: PermissionItem;
}

export type FirebaseMessageType =
  | 'appLog'
  | 'ediscovery'
  | 'driveSearchCore'
  | `driveSearchUserCore-${string}`
  | 'reports'
  | 'notifications_applog'
  | 'userSearch'
  | 'orgUserSearch'
  | 'contactSyncRefresh'
  | 'userListRefresh'
  | 'exportFinished'
  | 'groupMemberDelete'
  | 'role'
  | 'keepAlive'
  | 'indexingChange'
  | 'indexingError'
  | 'customMessages'
  | 'createdBuilding'
  | 'deletedBuilding'
  | 'userIndexStarted'
  | 'userIndexFinished'
  | 'orgIndexStarted'
  | 'orgIndexFinished'
  | 'groupIndexStarted'
  | 'groupIndexFinished'
  | 'calendarIndexStarted'
  | 'calendarIndexFinished'
  | 'featureFlagsUpdated'
  | 'test'
  | 'indexedUserUpdated'
  | 'bulkOpFinished'
  | 'loginActivityFinished'
  | 'groupsAndAccessFinished';

export interface FirebaseMessage {
  id: number;
  message: string;
  type: FirebaseMessageType;
  TTL: number;
  docLink?: string;
}

export interface FBNotificationMessage {
  createdDate: number;
  performingUser: string;
  action: string;
  target: string;
  ipaddress: string;
  permission: string;
}

export interface UserSearchFilter {
  orderBy: string;
  sortOrder: 'ASCENDING' | 'DESCENDING';
  searchModel: Partial<User>;
  messageType: FirebaseMessageType;
}

export interface Member {
  email: string;
  role: 'OWNER' | 'MANAGER' | 'MEMBER';
  canActOn: boolean;
}

export interface AddUserGroup {
  groupId: string;
  member: Member;
}

export interface WelcomeTemplateEntity {
  id: number;
  createdDate: number;
  templateName: string;
  description: string;
  templateSubject: string;
  templateHtml: string;
  default: boolean;
  readonly: boolean;
}

export interface SyncData {
  value: number;
  dateOnly: boolean;
  tzShift: number;
}

type MobileDeviceStatus =
  | 'APPROVED'
  | 'BLOCKED'
  | 'PENDING'
  | 'UNKNOWN'
  | 'UNPROVISIONED'
  | 'WIPED'
  | 'WIPING';

export interface MobileDevice {
  actOn: boolean;
  abdStatus: boolean;
  applications: Application[] | null;
  basebandVersion: string;
  brand: string;
  buildNumber: string;
  defaultLanguage: string;
  developerOptionsStatus: boolean;
  deviceCompromisedStatus: string;
  devicePasswordStatus: string;
  deviceId: string;
  encryptionStatus: string;
  email: string[];
  firstSync: SyncData;
  hardware: string;
  hardwareId: string;
  imei: string;
  isForCompanyUseOnly: boolean;
  kernelVersion: string;
  lastSync: SyncData;
  managedAccountIsOnOwnerProfile: boolean;
  manufacturer: string;
  meid: string;
  model: string;
  name: string[];
  networkOperator: string;
  otherAccountsInfo: string[];
  os: string;
  privilege: string;
  releaseVersion: string;
  resourceId: string;
  securityPatchLevel: string;
  serialNumber: string;
  status: MobileDeviceStatus;
  supportsWorkProfile: boolean;
  type: string;
  unknownSourcesStatus: boolean;
  userAgent: string;
  wifiMacAddress: string;
}

export type MobileDeviceAction =
  | 'APPROVE'
  | 'DELETE'
  | 'BLOCK'
  | 'WIPE'
  | 'WIPE_ACCOUNT';

export interface Application {
  packageName: string;
  displayName: string;
  versionName: string;
  versionCode: number;
  permissions: string[];
}

export interface PolicyActionModel {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  actionData: Record<string, any>;
  action: string;
  gmailFilter?: FilterModel | null;
  userModelTemplate?: Partial<User>;
  hasRun: boolean;
  id: number;
}

export interface Schedulable {
  id: number;
  notifyViaEmail?: boolean;
  daysOfWeek: boolean;
  emailsToNotify: string[] | null;
  emailsToShare: string[] | null;
  times: string[] | null;
  dates: string[] | null;
  scheduleEndDate: number;
}

export interface Policy extends Schedulable {
  createdBy: string | null;
  createdDate: number;
  lastRunDate: number;
  policyName: string;
  description: string;
  manualRun: boolean;
  scheduled: boolean;
  useRunDate: boolean;
  runDate: number | null;
  runWithoutSaving: boolean;
  sourceType:
    | 'All_Users'
    | 'All_Groups'
    | 'User'
    | 'Group'
    | 'OrgUnit'
    | 'gPanel_Role'
    | 'Mobile_Device'
    | 'Domain';
  policyType: 'standard' | 'newUser';
  actions: string[];
  sources: string[];
  actionHash: {
    [key: string]: PolicyActionModel;
  };
  runImmediately: boolean;
  allSource: boolean;
  targetsExcluded: string[];
  status?: ReportStatus;
}

export interface EmailMonitor {
  requestId: string;
  listeningUser: string;
  monitoredUser: string;
  startDate: number;
  startDay: number;
  startMonth: number;
  startYear: number;
  endDate: number;
  endDay: number;
  endMonth: number;
  endYear: number;
  fullOutgoing: string;
  fullIncoming: string;
  draftMonitoring: string;
  chatMonitoring: string;
}

export interface AppPassword {
  codeId: number;
  name: string;
  userId: string;
  creationTime: number;
  lastTimeUsed: number;
}

export interface ThirdPartyApp {
  clientId: string;
  displayText: string;
  anonymous: boolean;
  nativeApp: boolean;
  userId: string;
  scopes: string[];
}

interface ChatInfo {
  chatNotification?: boolean;
  chatUrl: string;
  chatMessage: string;
}

interface ActionInfo {
  actionTodo: string[];
  payload: string;
  endpointUrl: string;
  requestMethod: string;
  newPassword: string;
  timeZone: string;
  occurrenceLimit: number;
}

interface EmailInfo {
  emailNotificationEmails: string[];
  ccEmails: string[];
  bccEmails: string[];
  emailSubject: string;
  emailMessage: string;
}

export interface BasicInfo {
  createdDate: number;
  ruleName: string;
  description: string;
  status: 'Enabled' | 'Disabled' | 'Draft';
  creator: string;
  lastModifiedDate: number;
}

export interface PolicyInfo {
  policyId: number;
  policyType: string;
}

// form sends as a number (correctly), server saves it as a string
export interface GroupSettingsInfo {
  groupTemplateId: string | number;
}

interface DriveFolderTriggerSource {
  driveFolderOwner: string;
  driveFolderId: string;
}

export interface TriggerInfo {
  triggerGroup: string;
  triggerEvent: string;

  occursEverytime: boolean;
  hasTimeDuration: boolean;

  triggerThreshold: number;

  triggerUserSources: string[];
  triggerGroupSources: string[];
  triggerOrgSources: string[];
  triggerDomainSources: string[];
  triggerCalendarSources: string[];

  triggerSourceGroup?: string;

  triggerDuration: number;
  triggerDurationType: string;

  sharedDriveIds: string[];
  driveFolderTriggerSources: DriveFolderTriggerSource[];
  driveRegExs: string[];

  driveFileSizeFloor: number;
  driveFileSizeCeiling: number;
  fileMimeTypes: string[];

  fileIsSharedToDomain: boolean;
  fileIsSharedPublic: boolean;
  fileSharedToSpecificUser: string[];
  canFindInSearchResults: boolean;
  mustHaveLinkToAccess: boolean;
  fileSharedToUserExternalToDomain: boolean;
  fileSharedToUserInternalToDomain: boolean;
}

export interface ProfileFieldFilter {
  profileFieldType?: string;
  profileFieldValue?: string;
  profileFieldValues?: string[];
  profileFieldValueExact?: boolean;
  profileFieldRegex?: string;
  fieldDataType?: string;
}

export interface GroupInfo {
  groupEmail: string[];
  groupRole: string[];
}

export interface RemoveGroupInfo {
  groupEmail: string[];
}

export interface GroupFilter {
  hasGroupNameFilter?: boolean;
  groupNameMode?: 'name' | 'regex';
  groupName?: string;
  groupNameExact?: boolean;
  groupNameRegex?: string;
  hasMemberCountFilter?: boolean;
  memberCountMode?: 'single' | 'range';
  memberCountOperator?:
    | 'equals'
    | 'greaterThan'
    | 'lessThan'
    | 'greaterThanOrEqual'
    | 'lessThanOrEqual';
  memberCount?: number;
  memberCountRangeLessThan?: number;
  memberCountRangeGreaterThan?: number;
}

export interface Condition {
  id: string;
  fieldInfoType: string;
  fieldName: string;
  operator:
    | 'contains'
    | 'equals'
    | 'notContains'
    | 'notEquals'
    | 'startsWith'
    | 'endsWith'
    | 'larger'
    | 'smaller'
    | 'between'
    | 'greaterThan'
    | 'greaterThanOrEqual'
    | 'lessThan'
    | 'lessThanOrEqual'
    | 'isEmpty'
    | 'isNotBetween';
  value: string;
  secondaryValue?: string;
  unit?: string;
  logicalOperator?: 'AND' | 'OR';
}

export interface ConditionBlock {
  id: string;
  conditions: Condition[];
}
export interface PersonalCalendarFilter {
  hasPersonalCalendarFilter?: boolean;
  sharedToUserExternalToDomain?: boolean;
  sharedToUserInternalToDomain?: boolean;
  sharedToSpecificUsers?: string[];
  sharedToGroups?: string[];
}

export interface RuleModel {
  id: number;
  totalExecutions?: number;
  basicInfo: BasicInfo;
  triggerInfo: TriggerInfo;
  actionInfo: ActionInfo;
  emailInfo: EmailInfo;
  chatInfo: ChatInfo;
  policyInfo: PolicyInfo;
  groupSettingsInfo: GroupSettingsInfo;
  groupInfo?: GroupInfo;
  removeGroupInfo?: RemoveGroupInfo;
  profileFieldFilter?: ProfileFieldFilter;
  conditionBlocks?: ConditionBlock[];
  groupFilter?: GroupFilter;
  personalCalendarFilter?: PersonalCalendarFilter;
  allSource?: boolean;
  targetsExcluded?: string[];
}

export interface RulesEngineRunLog {
  ruleId: number;
  ruleName: string;
  startTime: number;
  endTime: number;
  status: 'started' | 'success' | 'failed' | 'skipped';
  id: number;
}

export interface RulesEngineActionLog {
  id: number;
  ruleId: number;
  createdDate: number;
  actionString: string;
  actionType:
    | 'none'
    | 'suspendUser'
    | 'runPolicy'
    | 'callEndpoint'
    | 'changePassword'
    | 'changeCalendarTimezone'
    | 'resetPassword'
    | 'signOut'
    | 'sendEmail'
    | 'sendChatMessage'
    | 'applyGroupTemplate'
    | 'limitOccurrences'
    | 'addToGroup'
    | 'sendEmailTemplate'
    | 'deleteGroup'
    | 'removeFromGroup'
    | 'removeShare';
  target: string;
  status: 'success' | 'failed';
  resolutionType: 'policy' | null | undefined;
}

export interface Calendar {
  id: string;
  summary: string;
  description: string | null;
  location: string | null;
  timeZone: string;
  summaryOverride: string | null;
  color: string;
  colorId: string;
  backgroundColor: string;
  foregroundColor: string;
  hidden: boolean;
  selected: boolean;
  accessRole: 'freeBusyReader' | 'reader' | 'writer' | 'owner';
  primary: boolean;
  defaultReminders: {
    method: 'email' | 'popup' | 'sms';
    minutes: number;
  }[];
}

interface CalendarEventCreator {
  id?: string;
  email: string | null;
  displayName?: string | null;
  self?: boolean;
}

interface CalendarEventDateTime {
  allDay: boolean;
  dateTime: number;
  timeZone?: string;
  year?: number;
  month?: number;
  date?: number;
  hour?: number;
  minute?: number;
}

interface CalendarEventAttendee {
  id?: string;
  displayName?: string;
  email: string;
  organizer?: boolean;
  self?: boolean;
  resource?: boolean;
  optional?: boolean;
  responseStatus?: 'needsAction' | 'declined' | 'tentative' | 'accepted';
  comment?: string;
  additionalGuests?: number;
}

interface CalendarEventGadget {
  type: string;
  title: string;
  link: string;
  iconLink: string;
  width: number;
  height: number;
  display: 'icon' | 'chip';
  preferences: {
    [key: string]: string;
  };
}

interface CalendarReminderModel {
  method: 'email' | 'popup' | 'sms';
  minutes: number;
}

export interface CalendarEventModel {
  id: string;
  status: 'confirmed' | 'tentative' | 'cancelled';
  htmlLink: string;
  created: number;
  updated: number;
  summary: string | null;
  description: string | null;
  location: string | null;
  colorId: string | null;
  creator: CalendarEventCreator;
  organizer: CalendarEventCreator;
  start: CalendarEventDateTime;
  end: CalendarEventDateTime;
  endTimeUnspecified: false;
  recurrence: string[];
  recurringEventId: string | null;
  originalStartTime: CalendarEventDateTime;
  transparency: 'opaque' | 'transparent';
  visibility: 'Default' | 'Public' | 'Private' | 'Confidential';
  iCalUID: string;
  sequence: number;
  attendees: CalendarEventAttendee[];
  attendeesOmitted: boolean;
  extendedProperties: {
    privateMap: {
      [key: string]: string;
    };
    sharedMap: {
      [key: string]: string;
    };
  };
  gadget: CalendarEventGadget;
  anyoneCanAddSelf: boolean;
  guestsCanInviteOthers: boolean;
  guestsCanSeeOtherGuests: boolean;
  privateCopy: boolean;
  reminders: CalendarReminderModel;
  locked: boolean;
  hangoutLink: string;
  source: {
    url: string;
    title: string;
  };
}

export interface CalendarSearch {
  summary: string;
  fieldsToReturn: string[];
  searchableEntities: string[];
  rowHtml: string;
  displayString: string;
}

export interface CalendarAclResource {
  id?: string;
  role:
    | 'none'
    | 'freeBusyReader'
    | 'reader'
    | 'writer'
    | 'writerWithoutPrivateAccess'
    | 'owner';
  scope: {
    type: 'default_public' | 'user' | 'group' | 'domain';
    value?: string;
  };
  canActOn: boolean;
  toBeDeleted: boolean;
}

export interface SpreadSheetColumn {
  columnName: string;
  valueName: string;
  name: string;
  isCanned: boolean;
  useInReport: boolean;
  showInColumnPicker: boolean;
  type?: string;
  requiredInReport: boolean;
  requiredInBulkOps: boolean;
}

type ReportName =
  | 'resourceCalendarReport'
  | 'abandonedCalendarsReport'
  | 'activitiesSharedReportPublic'
  | 'activitiesSharedReportPublicSharedDrives'
  | 'activitiesSharedReportMyDrive'
  | 'activitiesSharedReportSharedDrive'
  | 'approvedEmailForwardingReport'
  | 'archiveUsers'
  | 'calendarAccessReport'
  | 'calendarEventsReport'
  | 'recurringResourceCalendarEventsReport'
  | 'calendarPermissionSettingsReport'
  | 'customersUsage'
  | 'docActivity'
  | 'docsModOver14'
  | 'docsModOverXdays'
  | 'docsOver100MB'
  | 'docsOver10MB'
  | 'docsSharedExternally'
  | 'docsSharedPublicly'
  | 'docsSharedWithGroups'
  | 'docsSharedWithDomain'
  | 'docsSharedWithLink'
  | 'docsWithMP3Ext'
  | 'documentsCreated'
  | 'EmailActivityReport'
  | 'emailDelegate'
  | 'emailForwarding'
  | 'emailMonitor'
  | 'emptyGroupReport'
  | 'exportAdminLogs'
  | 'appLogExport'
  | 'exportPersonalContacts'
  | 'externallyOwnedDocs'
  | 'gmailBreakdownReport'
  | 'gmailUsageUnder1MB'
  | 'gPanelRoleReport'
  | 'groupReport'
  | 'userGroupReport'
  | 'groupMemberCountReport'
  | 'groupMembershipReport'
  | 'nestedGroupReport'
  | 'groupsWithNestedGroupsReport'
  | 'groupsWithNoOwnersReport'
  | 'hangoutsMeet'
  | 'hiddenUser'
  | 'inactiveUsers'
  | 'lastLogin'
  | 'licenseTypes'
  | 'listUserGmailFilters'
  | 'loginActivity'
  | 'timeAndIPAddressOfLoggedInUser'
  | 'mobileAppsReport'
  | 'mobileDeviceDump'
  | 'orgMemberCount'
  | 'emptyOrg'
  | 'creditCardAndSSNReport'
  | 'sendAsReport'
  | 'sharedContacts'
  | 'sharedDrives'
  | 'sharedDrivesDocsSharedExternally'
  | 'signature'
  | 'signatureTemplateBlackListStatusReport'
  | 'signatureTemplateExport'
  | 'storageReport'
  | 'superAdmin'
  | 'suspendedUser'
  | 'thirdPartyApplicationReport'
  | 'twoFactorEnrolled'
  | 'unmodifiedDocumentsReport'
  | 'userprofile'
  | 'userUsage'
  | 'usersWhoAreNotMemberOfAnyGroup';

export interface ReportType {
  name: ReportName;
  niceName: string;
  columns: SpreadSheetColumn[];
  possibleSources: string[];
  showInUserManagement: boolean;
  showInGroupManagement: boolean;
  role: string;
  includeInStarter: boolean;
  includedInCoreAndBeyond: boolean;
  description: string;
  hasDateSelection: boolean;
  hasDateRange: boolean;
  hasAdditionalConfiguration: boolean;
  category: string;
  hasColumns: boolean;
  hasPossibleSources: boolean;
}

export type ReportStatus = 'IDLE' | 'RUNNING' | 'COMPLETED';

export interface ReportModel extends Schedulable {
  createdDate: number;
  lastRunDate: number;
  reportName: string;
  description: string;
  createdBy: string;
  runWithoutSaving: boolean;
  manualRun: boolean;
  scheduled: boolean;
  sourceType:
    | 'All_Users'
    | 'All_Groups'
    | 'User'
    | 'Group'
    | 'OrgUnit'
    | 'gPanel_Role'
    | 'Mobile_Device'
    | 'Domain'
    | 'Resource_Calendar'
    | 'Shared_Drive'
    | null;
  sources: string[] | null;
  //Spreadsheet column is interface
  columns: SpreadSheetColumn[] | null;
  reportType: string;
  destFolderId: string | null;
  destFolderName: string | null;
  destFolderUrl: string | null;
  destFolderMimeType: string | null;
  spreadsheetId: string | null;
  runImmediately: boolean;
  useReportName: boolean;
  autoShare: boolean;
  dynamicReportName: boolean;
  allSource: boolean;
  startDate: number;
  endDate: number;
  _notifyViaEmail: boolean;
  externalEmailAddress: string | null;
  daysback: number | null;
  reportOnLastXDays: number;
  regexTypeSelectedList: string[] | null;
  permissions: PermissionsModel[] | null;
  regex: string | null;
  neverLoggedInCheck: boolean;
  inactiveSinceDate: number;
  generatedFileName: string;
  domainPinnedAt: number | null;
  pinnedPersonally: PersonalPin[] | null;
  status: ReportStatus;
}

export interface Page<T> {
  cursor: string | null;
  firstPull?: boolean;
  nameSpace?: string | null;
  pageSize: number;
  results: T;
  searchTime?: number;
  allFinished?: boolean;
  queries?: {
    [key: string]: string;
  };
}

export interface Folder extends Doc {
  children?: Folder[];
}

export interface DocOwner {
  displayName: string;
  pictureURL: string;
  permissionId: string;
  emailAddress: string;
  isAuthenticatedUser: boolean;
}

export interface Doc {
  date: string;
  domain: string;
  id: string;
  title: string;
  mimeType: string;
  desc: string;
  createdTime: number;
  modifiedDate: number;
  fileSize: number;
  lastModifyingUserName: string;
  trashed: boolean;
  parentId: string;
  starred: boolean;
  owner: string;
  ownerEmail: string;
  isShared: boolean;
  iconUrl: string;
  thumbnailLink: string;
  alternateLink: string;
  owners: DocOwner[];
  foundUnder: string;
  // this is the shared drive ID
  driveId: string | null;
  shortcutDetails: ShortcutDetails;
  location: DocLocation | null;
  labelTitles: string[] | null;
  labelIds: string[] | null;
  labelChoiceIds: string[][] | null;
  permissions: PermissionsModel[] | null;
  // Whether the current user can edit this file
  canEdit?: boolean;
}

export interface ShortcutDetails {
  targetId: string;
  targetMimeType: string;
  location: DocLocation | null;
  targetAccessible: boolean;
  targetTrashed: boolean;
}

export interface DocLocation {
  id: string;
  name: string;
  mimeType: string;
  iconLink: string;
  isSharedWithMe: string;
  isSharedDrive: string;
  docOwner: string;
  hasAccess: string;
}

export interface TransferOwnershipModel {
  domain?: string;
  startedBy?: string;
  srcUser: string;
  srcFolderId?: string;
  destUser: string;
  destFolderId?: string;
  notifyByEmail: boolean;
  emailsToNotify?: string[];
  action?:
    | 'editor'
    | 'viewer'
    | 'removeOld'
    | 'removeAll'
    | 'commenter'
    | 'noAction';
}

interface LabelColor {
  backgroundColor: string;
  textColor: string;
}
export interface LabelModel {
  id: string;
  name: string;
  type: string;
  messagesTotal: number;
  messagesUnread: number;
  threadsTotal: number;
  threadsUnread: number;
  messageVisibility: 'hide' | 'show';
  labelVisibility: 'labelShow' | 'labelHide' | 'labelShowIfUnread';
  color: LabelColor | null;
}

export interface DelegateModel {
  delegateEmail: string;
  verificationStatus: string;
}

export interface ExpiringGmailDelegate {
  email: string;
  delegateEmail: string;
  expirationTime: number;
  createdTime: number;
  creatorEmail: string;
}

export interface ForwardingAddressModel {
  forwardingEmail: string;
  verificationStatus: string;
}

export interface ImapSettingsModel {
  enabled: boolean;
  autoExpunge: boolean;
  expungeBehavior: string;
  maxFolderSize: number;
}

export interface ForwardingModel {
  emailAddress: string;
  disposition: string;
  enabled: boolean;
}

export interface PopSettingsModel {
  accessWindow: string;
  disposition: string;
}

export interface SmtpMsaModel {
  host: string;
  port: number;
  username: string;
  password: string;
  securityMode: string;
}

export interface SendAsModel {
  sendAsEmail: string;
  displayName: string;
  replyToAddress: string;
  signature?: string | null;
  isPrimary: boolean;
  isDefault: boolean;
  treatAsAlias: boolean;
  smtpMsa?: SmtpMsaModel;
  verificationStatus?: string;
}

export interface VacationModel {
  enableAutoReply: boolean;
  responseSubject: string | null;
  responseBodyPlainText?: string | null;
  responseBodyHtml: string | null;
  restrictToContacts: boolean;
  restrictToDomain: boolean;
  startTime: number | null;
  endTime: number | null;
}

export interface CriteriaModel {
  from: string;
  to: string;
  subject: string;
  query: string;
  negatedQuery: string;
  hasAttachment: boolean;
  excludeChats: boolean;
  size: number;
  sizeComparison: string;
  sizeType: string;
}

export interface ActionModel {
  addLabelIds: string[];
  removeLabelIds: string[];
  forward: string;
}

export interface FilterModel {
  id?: string | null;
  criteria: CriteriaModel;
  action: ActionModel;
}

export interface MailIdModel {
  id: string;
  threadId: string;
}

export interface SignatureModel extends Schedulable {
  createdDate: number;
  lastRunDate: number | null;
  templateName: string;
  description: string;
  templateHtml: string;
  manualRun: boolean;
  scheduled: boolean;
  sourceType:
    | 'All_Users'
    | 'All_Groups'
    | 'User'
    | 'Group'
    | 'OrgUnit'
    | 'gPanel_Role'
    | 'Mobile_Device'
    | 'Domain';
  sources: string[];
  runImmediately: boolean;
  allSource: boolean;
  loadedSchedule: boolean;
  runOnDefaultAlias: boolean;
  runOnPrimaryAlias: boolean;
  runOnAliases: boolean;
  useSendAsAccountForTemplate: boolean;
}

export interface SignatureRunLog {
  signatureName: string;
  signatureKeyString: string;
  signatureId: number;
  startTime: number;
  endTime: number;
  isManual: boolean;
  targetEmails: string[];
  pushedToEmails: string[];
}

export interface ContactSyncRunLog {
  id: number;
  contactSyncName: string;
  contactSyncKeyString: string;
  contactSyncId: number;
  startTime: number;
  endTime: number;
  isManual: boolean;
  targetEmails: string[];
  pushedToEmails: string[];
  status: 'IN_PROGRESS' | 'SUCCESS' | 'FAILED' | 'PARTIAL_SUCCESS';
  errorMessage: string | null;
  contactsProcessed: number;
  contactsSucceeded: number;
  contactsFailed: number;
}

type AppLogType =
  | 'All_Users'
  | 'All_Groups'
  | 'Application'
  | 'Building'
  | 'Feature'
  | 'Calendar'
  | 'Contact'
  | 'Domain'
  | 'Error'
  | 'gPanel_Role'
  | 'Group'
  | 'Mobile_Device'
  | 'OrgUnit'
  | 'Site'
  | 'User'
  | 'Add_License'
  | 'Add_gPanel_License'
  | 'Migrated'
  | 'API';

export interface AppLog {
  createdDate: number;
  performingUser: string;
  action: string;
  target: string;
  ipaddress: string;
  changeLogReason: string | null;
  permission: string;
  permissionNiceName: string;
  parentId: string;
  id: string;
}

export interface AppLogSearch {
  startedDate: number;
  endDate: number;
  performingUser: string;
  target: string;
  appLogType: AppLogType;
  module:
    | 'application'
    | 'auditCompliance'
    | 'riskManagement'
    | 'discovery'
    | 'reporting'
    | 'security'
    | 'tools'
    | 'bulkOp'
    | 'administration'
    | 'export'
    | 'users'
    | 'directory'
    | 'chromeManagementAPI'
    | 'calendars'
    | 'buildings'
    | 'features'
    | 'drive'
    | 'sites'
    | 'userPortal';
  permissionRequired: string;
  ipAddress: string;
  parentId: number;
}

export interface SignatureRequestBody {
  template: string;
  user: User;
  sendAs: SendAsModel;
  requestTime: number;
}

export interface SignaturePopulationResponse {
  populatedTemplate: string;
  requestTime: number;
}

export interface GPanelPermissionsModel extends PermissionsModel {
  changeType: 'add' | 'change' | 'delete' | null;
}

type PermissionLevel = 0 | 1 | 2 | 3 | 4 | 5;

interface PermissionDetail {
  permissionType: string;
  inheritedFrom: string;
  role: string;
  inherited: boolean;
}

export interface PermissionsModel {
  permissionLevel?: PermissionLevel;
  // niceName: string;
  // driveNiceName: string;
  userEmail: string | null;
  displayName: string | null;
  permId: string;
  role:
    | 'owner'
    | 'organizer'
    | 'fileOrganizer'
    | 'writer'
    | 'commenter'
    | 'reader';
  additionalRoles: string[];
  type: 'user' | 'group' | 'domain' | 'anyone';
  withLink: boolean;
  isCommenter: boolean;
  photoLink: string;
  docId: string;
  docOwner: string;
  docTitle: string;
  mimeType: string;
  isPrimaryOwner: boolean;
  toBeDeleted: boolean;
  sendNotification: boolean;
  transferOwnership: boolean;
  permExpirationTime: number | null;
  allowFileDiscovery: boolean;
  permissionDetails: PermissionDetail[];
}

export interface GmailPopModel {
  enable: boolean;
  enableFor: string;
  action: string;
}

export interface NumbericIndexingSpec {
  minValue: number;
  maxValue: number;
}

export interface FieldSpec {
  displayName: string;
  etag: string;
  fieldId: string;
  fieldName: string;
  fieldType: string;
  indexed: boolean;
  multiValued: boolean;
  reacAccessType: string;
  numericIndexing: NumbericIndexingSpec;
}

export interface CustomSchemaEntity {
  displayName: string;
  etag: string;
  fields: FieldSpec[];
  schemaId: string;
  schemaName: string;
}

interface BackgroundImageFile {
  id: string;
  width: number;
  xCoordinate: number;
  yCoordinate: number;
}

interface Capabilities {
  canAddChildren: boolean;
  canChangeCopyRequiresWriterPermissionRestriction: boolean;
  canChangeDomainUsersOnlyRestriction: boolean;
  canChangeDriveBackground: boolean;
  canChangeDriveMembersOnlyRestriction: boolean;
  canComment: boolean;
  canCopy: boolean;
  canDeleteChildren: boolean;
  canDeleteDrive: boolean;
  canDownload: boolean;
  canEdit: boolean;
  canListChildren: boolean;
  canManageMembers: boolean;
  canReadRevisions: boolean;
  canRename: boolean;
  canRenameDrive: boolean;
  canShare: boolean;
  canTrashChildren: boolean;
}

export interface Restrictions {
  adminManagedRestrictions: boolean;
  copyRequiresWriterPermission: boolean;
  domainUsersOnly: boolean;
  driveMembersOnly: boolean;
  sharingFoldersRequiresOrganizerPermission: boolean;
}

export interface SharedDriveModel {
  kind: string;
  id: string;
  name: string;
  themeId: string;
  colorRgb: string;
  backgroundImageFile: BackgroundImageFile;
  backgroundImageLink: string;
  capabilities: Capabilities;
  createdDate: number;
  hidden: boolean;
  restrictions: Restrictions;
  orgUnitId: string | null;
  orgUnitPath: string | null;
}

export interface BigQueryPage<T> {
  results: T;
  pageSize: number;
  currentPage: number;
  totalCount: number;
}

export interface DirectoryGroup {
  id?: string;
  email: string;
  name: string;
  description: string;
  adminCreated?: boolean;
  aliases?: string[];
  nonEditableAliases?: string[];
  directMembersCount?: number;
}

export interface GroupTemplate {
  id: number;
  keyString?: string;
  createdBy?: string;
  createdDate: number;
  groupTemplateName: string;
  groupTemplateDesc: string;
  whoCanJoin:
    | 'ANYONE_CAN_JOIN'
    | 'ALL_IN_DOMAIN_CAN_JOIN'
    | 'CAN_REQUEST_TO_JOIN'
    | 'INVITED_CAN_JOIN';
  whoCanViewMembership:
    | 'ALL_MANAGERS_CAN_VIEW'
    | 'ALL_MEMBERS_CAN_VIEW'
    | 'ALL_IN_DOMAIN_CAN_VIEW'
    | 'ALL_OWNERS_CAN_VIEW';
  whoCanViewGroup:
    | 'ANYONE_CAN_VIEW'
    | 'ALL_MANAGERS_CAN_VIEW'
    | 'ALL_MEMBERS_CAN_VIEW'
    | 'ALL_IN_DOMAIN_CAN_VIEW'
    | 'ALL_OWNERS_CAN_VIEW';
  allowExternalMembers: boolean;
  whoCanPostMessage:
    | 'NONE_CAN_POST'
    | 'ALL_MANAGERS_CAN_POST'
    | 'ALL_MEMBERS_CAN_POST'
    | 'ALL_IN_DOMAIN_CAN_POST'
    | 'ALL_OWNERS_CAN_POST'
    | 'ANYONE_CAN_POST';
  allowWebPosting: boolean;
  isArchived: boolean;
  messageModerationLevel:
    | 'MODERATE_ALL_MESSAGES'
    | 'MODERATE_NEW_MEMBERS'
    | 'MODERATE_NON_MEMBERS'
    | 'MODERATE_NONE';
  spamModerationLevel: 'MODERATE' | 'SILENTLY_MODERATE' | 'REJECT' | 'ALLOW';
  replyTo: string;
  customReplyTo: string;
  sendMessageDenyNotification: boolean;
  defaultMessageDenyNotificationText: string;
  membersCanPostAsTheGroup: boolean;
  includeInGlobalAddressList: boolean;
  whoCanModerateMembers:
    | 'ALL_MEMBERS'
    | 'OWNERS_AND_MANAGERS'
    | 'OWNERS_ONLY'
    | 'NONE';
  archiveOnly: boolean;
  whoCanModerateContent:
    | 'ALL_MEMBERS'
    | 'OWNERS_AND_MANAGERS'
    | 'OWNERS_ONLY'
    | 'NONE';
  whoCanAssistContent:
    | 'ALL_MEMBERS'
    | 'OWNERS_AND_MANAGERS'
    | 'OWNERS_ONLY'
    | 'MANAGERS_ONLY'
    | 'NONE';
  primaryLanguage:
    | 'Arabic'
    | 'Bengali'
    | 'Bulgarian'
    | 'Catalan'
    | 'Chinese_Simplified'
    | 'Chinese_Traditional'
    | 'Croatian'
    | 'Czech'
    | 'Danish'
    | 'Dutch'
    | 'English_United_States'
    | 'English_United_Kingdom'
    | 'Estonian'
    | 'Finnish'
    | 'French'
    | 'German'
    | 'Greek'
    | 'Gujarati'
    | 'Hebrew'
    | 'Hindi'
    | 'Hungarian'
    | 'Icelandic'
    | 'Indonesian'
    | 'Italian'
    | 'Japanese'
    | 'Kannada'
    | 'Korean'
    | 'Latvian'
    | 'Lithuanian'
    | 'Malay'
    | 'Malayalam'
    | 'Marathi'
    | 'Norwegian'
    | 'Oriya'
    | 'Persian'
    | 'Polish'
    | 'Portuguese_Brazil'
    | 'Portuguese_Portugal'
    | 'Romanian'
    | 'Russian'
    | 'Serbian'
    | 'Slovak'
    | 'Slovenian'
    | 'Spanish'
    | 'Swedish'
    | 'Tagalog'
    | 'Tamil'
    | 'Telugu'
    | 'Thai'
    | 'Turkish'
    | 'Ukrainian'
    | 'Urdu'
    | 'Vietnamese';
  whoCanLeaveGroup:
    | 'ALL_MANAGERS_CAN_LEAVE'
    | 'ALL_MEMBERS_CAN_LEAVE'
    | 'NONE_CAN_LEAVE'
    | 'ALL_OWNERS_CAN_LEAVE';
  whoCanContactOwner:
    | 'ALL_IN_DOMAIN_CAN_CONTACT'
    | 'ALL_MANAGERS_CAN_CONTACT'
    | 'ALL_MEMBERS_CAN_CONTACT'
    | 'ANYONE_CAN_CONTACT';
  whoCanDiscoverGroup:
    | 'ANYONE_CAN_DISCOVER'
    | 'ALL_IN_DOMAIN_CAN_DISCOVER'
    | 'ALL_MEMBERS_CAN_DISCOVER';
  securityQuery?: string;
}

export interface GroupBusinessSettings {
  email: string;
  name: string;
  description: string;
  whoCanJoin:
    | 'ANYONE_CAN_JOIN'
    | 'ALL_IN_DOMAIN_CAN_JOIN'
    | 'CAN_REQUEST_TO_JOIN'
    | 'INVITED_CAN_JOIN';
  whoCanViewMembership:
    | 'ALL_MANAGERS_CAN_VIEW'
    | 'ALL_MEMBERS_CAN_VIEW'
    | 'ALL_IN_DOMAIN_CAN_VIEW'
    | 'ALL_OWNERS_CAN_VIEW';
  whoCanViewGroup:
    | 'ANYONE_CAN_VIEW'
    | 'ALL_MANAGERS_CAN_VIEW'
    | 'ALL_MEMBERS_CAN_VIEW'
    | 'ALL_IN_DOMAIN_CAN_VIEW'
    | 'ALL_OWNERS_CAN_VIEW';
  allowExternalMembers: boolean;
  whoCanPostMessage:
    | 'NONE_CAN_POST'
    | 'ALL_MANAGERS_CAN_POST'
    | 'ALL_MEMBERS_CAN_POST'
    | 'ALL_IN_DOMAIN_CAN_POST'
    | 'ALL_OWNERS_CAN_POST'
    | 'ANYONE_CAN_POST';
  allowWebPosting: boolean;
  primaryLanguage:
    | 'Arabic'
    | 'Bengali'
    | 'Bulgarian'
    | 'Catalan'
    | 'Chinese_Simplified'
    | 'Chinese_Traditional'
    | 'Croatian'
    | 'Czech'
    | 'Danish'
    | 'Dutch'
    | 'English_United_States'
    | 'English_United_Kingdom'
    | 'Estonian'
    | 'Finnish'
    | 'French'
    | 'German'
    | 'Greek'
    | 'Gujarati'
    | 'Hebrew'
    | 'Hindi'
    | 'Hungarian'
    | 'Icelandic'
    | 'Indonesian'
    | 'Italian'
    | 'Japanese'
    | 'Kannada'
    | 'Korean'
    | 'Latvian'
    | 'Lithuanian'
    | 'Malay'
    | 'Malayalam'
    | 'Marathi'
    | 'Norwegian'
    | 'Oriya'
    | 'Persian'
    | 'Polish'
    | 'Portuguese_Brazil'
    | 'Portuguese_Portugal'
    | 'Romanian'
    | 'Russian'
    | 'Serbian'
    | 'Slovak'
    | 'Slovenian'
    | 'Spanish'
    | 'Swedish'
    | 'Tagalog'
    | 'Tamil'
    | 'Telugu'
    | 'Thai'
    | 'Turkish'
    | 'Ukrainian'
    | 'Urdu'
    | 'Vietnamese';
  isArchived: boolean;
  archiveOnly: boolean;
  messageModerationLevel:
    | 'MODERATE_ALL_MESSAGES'
    | 'MODERATE_NEW_MEMBERS'
    | 'MODERATE_NON_MEMBERS'
    | 'MODERATE_NONE';
  spamModerationLevel: 'MODERATE' | 'SILENTLY_MODERATE' | 'REJECT' | 'ALLOW';
  replyTo: string;
  customReplyTo: string;
  sendMessageDenyNotification: boolean;
  defaultMessageDenyNotificationText: string;
  membersCanPostAsTheGroup: boolean;
  includeInGlobalAddressList: boolean;
  whoCanLeaveGroup:
    | 'ALL_MANAGERS_CAN_LEAVE'
    | 'ALL_MEMBERS_CAN_LEAVE'
    | 'NONE_CAN_LEAVE'
    | 'ALL_OWNERS_CAN_LEAVE';
  whoCanContactOwner:
    | 'ALL_IN_DOMAIN_CAN_CONTACT'
    | 'ALL_MANAGERS_CAN_CONTACT'
    | 'ALL_MEMBERS_CAN_CONTACT'
    | 'ANYONE_CAN_CONTACT';
  whoCanModerateMembers:
    | 'ALL_MEMBERS'
    | 'OWNERS_AND_MANAGERS'
    | 'OWNERS_ONLY'
    | 'NONE';
  whoCanModerateContent:
    | 'ALL_MEMBERS'
    | 'OWNERS_AND_MANAGERS'
    | 'OWNERS_ONLY'
    | 'NONE';
  whoCanAssistContent:
    | 'ALL_MEMBERS'
    | 'OWNERS_AND_MANAGERS'
    | 'OWNERS_ONLY'
    | 'MANAGERS_ONLY'
    | 'NONE';
  whoCanDiscoverGroup:
    | 'ANYONE_CAN_DISCOVER'
    | 'ALL_IN_DOMAIN_CAN_DISCOVER'
    | 'ALL_MEMBERS_CAN_DISCOVER';
}

export interface CloudIdentityGroupRolesUpdateModel {
  groupId: string;
  membershipId: string;
  changeLogMessage: string;
  oldRoles: string[];
  newRole: string;
  hasMemberExpiry: boolean;
}

export interface GroupSecuritySettingsModel {
  memberRestriction: MemberRestriction;
  name: string;
  changeLogMessage: string;
  groupEmail: string;
}

export interface MemberRestriction {
  evaluation: MemberEvaluation;
  query: string;
}

export interface MemberEvaluation {
  state: string;
}

export interface DirectoryGroupMember {
  email: string;
  etag?: string;
  id?: string;
  role: 'MEMBER' | 'MANAGER' | 'OWNER';
  type?: string;
  canActOn: boolean;
}

export interface SharedContactModel {
  id: string;
  fullIdHref: string;
  userName?: string | null;
  userDomain?: string | null;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  prefix?: string | null;
  nickName: string | null;
  birthday: string | null;
  groupHref: string[];
  phoneNumbers: ProfileKind[] | null;
  addresses: ProfileKind[] | null;
  events: ProfileKind[] | null;
  emails: ProfileKind[] | null;
  webSites: ProfileKind[] | null;
  im: ProfileKind[] | null;
  relations: ProfileKind[] | null;
  orgs: ProfileKind[] | null;
  updated: number;
  hasBeenDeleted: boolean;
  extendedProperties: ExtendedPropertyModel[];
}

export interface ProfileKind {
  rel: string;
  val?: string | null;
  addrVal?: ProfileAddress | null;
  orgVal?: ProfileOrg | null;
}

export interface ProfileAddress {
  street: string | null;
  city: string | null;
  country: string | null;
  postCode: string | null;
  poBox: string | null;
  neighborhood: string | null;
  state: string | null;
  addressString: string;
}

export interface ProfileOrg {
  primary: boolean | null;
  orgString?: string | null;
  orgName: string | null;
  orgTitle: string | null;
  orgDepartment: string | null;
  orgJobDescription: string | null;
  orgSymbol: string | null;
  where: string | null;
}

export interface ExtendedPropertyModel {
  name: string;
  val: string;
}

export interface ContactSyncModel {
  id: number;
  userModelsCreated: boolean;
  name: string;
  description: string;
  addToMyContacts: boolean;
  lastRunTime: number;
  createdTime: number;
  sourceUser: string | null;
  groupTitle: string | null;
  groupHref: string | null;
  syncType: 'contact' | 'directory' | 'bidirectional';
  targetGroupName: string;
  targets: string[] | null;
  sources: string[] | null;
  targetType:
    | 'User'
    | 'Group'
    | 'Domain'
    | 'OrgUnit'
    | 'All_Users'
    | 'All_Groups';
  allSource: boolean;
  allSourceDirectorySync: boolean;
  runImmediately: boolean;
  v1KeyString: string;
  v2KeyString: string;
  migrated: boolean;
  scheduled: boolean;
  lastCompleteTime: number;
  lastError: string;
  trackSrcCreate: boolean;
  trackSrcUpdate: boolean;
  trackSrcDelete: boolean;
  trackDestCreate: boolean;
  trackDestUpdate: boolean;
  trackDestDelete: boolean;
  srcSyncToken: string;
  respectDirectoryContactSharing: boolean;
  isCurrentlyRunning: boolean;
  notifyViaEmail: boolean;
  emailsToNotify: string[];
  daysOfWeek: boolean;
  times: string[];
  dates: string[];
  scheduleEndDate?: number;
}

export interface ContactSyncUserModels {
  lastStartTime: number;
  userEmail: string;
  destGroupId: string;
  skip: boolean;
  destGroupCreationTime: number;
  lastCompleteTime: number;
  lastError: string;
  lastNumberOfDeletedContacts: number;
  lastNumberOfPushedContacts: number;
  initialSyncComplete: boolean;
  syncToken: string;
}

export interface DataTransferLog {
  dateCreated: number;
  transferId: string;
  srcUser: string;
  destUser: string;
  status: string;
  primaryDomain: string;
  applicationName: string;
  srcUserId: string;
  destUserId: string;
  emailsToNotify: string[];
  createdBy: string;
}

export interface ResourceTemplateModel {
  id: number;
  dateCreated: number;
  dateUpdated: number;
  createdBy: string | null;
  updatedBy: string | null;
  name: string;
  desc: string;
  calSharedToDomain: boolean;
  calPublic: boolean;
  domainRole: 'freeBusyReader' | 'reader';
  publicRole: 'freeBusyReader' | 'reader';
  shares: object;
  buildingId: string;
  floorName: string;
  resourceType: string;
  capacity: number | null;
  floorSection: string;
  resourceCategory: string;
}

export interface Coordinates {
  latitude: number | null;
  longitude: number | null;
}

export interface BuildingsModel {
  etags: string;
  buildingId: string;
  buildingName: string;
  description: string;
  coordinates: Coordinates | null;
  floorNames: string[];
  address: BuildingAddressModel | null;
}

export interface BuildingAddressModel {
  regionCode: string;
  languageCode: string | null;
  postalCode: string | null;
  administrativeArea: string | null;
  locality: string | null;
  sublocality: string | null;
  addressLines: string[] | null;
}

export interface FeatureModel {
  name: string;
  kind: string;
  etags: string;
}

export interface ResourceCalendar {
  etags: string;
  resourceId: string;
  resourceName: string;
  resourceType?: string | null;
  resourceDescription?: string | null;
  resourceEmail: string;
  generatedResourceName: string;
  capacity: number;
  buildingId: string;
  buildingName: string; // only for generated name -ag
  floorName: string;
  floorSection?: string | null;
  resourceCategory: string;
  userVisibleDescription?: string | null;
  featureInstances?: FeatureModel[] | null;
}

export interface DocSearchFilter {
  docOwnerEmail: string;
  title?: string;
  fullText: string;
  mimeType?: string;
  modifiedDateOp?: string | null;
  lastViewedOp?: string | null;
  timeZone: string;
  docID?: string;
  modifiedDate: Date | null;
  lastViewedByMeDate: Date | null;
  trashed: boolean;
  starred: boolean;
  hidden: boolean;
  onlySharedWithMe: boolean;
  onlyOwnedByMe: boolean;
  isExactTitle: boolean;
  allUsers: boolean;
  labels?: string[];
  sourceType:
    | 'All_Users'
    | 'All_Groups'
    | 'User'
    | 'Group'
    | 'OrgUnit'
    | 'gPanel_Role'
    | 'Mobile_Device'
    | 'Domain';
  sources: string[];
  sharedWithSources: string[];
  isSuspended?: boolean;
  parents: string[];
  owners: string[];
  writers: string[];
  readers: string[];
}

export interface DriveSweepModel {
  folderId: string;
  ownerId: string;
  primaryDomain: string;
  secondaryDomains: string[];
  lastError: string;
  name: string;
  desc: string;
  isMyDrive: boolean;
  sendShareNotifications: boolean;
  lastRun: number;
  created: number;
  lastUpdated: number;
  createdBy: string;
  updatedBy: string;
  folderName: string;
  isActive: boolean;
  isRunning: boolean;
}

export interface DriveMonitorJobLog {
  folderId: string;
  dateOfRun: number;
  successful: boolean;
  error: string;
  successCount: number;
  errorCount: number;
  jobStatus: string;
}

export interface RecentUsersModel {
  type: string;
  email: string;
}

export interface ActiveTimeRangesModel {
  activeTime: number;
  date: ChromeDeviceDate;
}

export interface DevicesFilesModel {
  name: string;
  type: string;
  downloadUrl: string;
  createTime: number;
}

export interface TpmVersionInfoModel {
  family: string;
  specLevel: string;
  manufacturer: string;
  tpmModel: string;
  firmwareVersion: string;
  vendorSpecific: string;
}

export interface CpuInfo {
  architecture: string;
  logicalCpus: CState[];
  maxClockSpeedKhz: number;
  model: string;
}

interface CState {
  cStates: CStateValue[];
  currentScalingFrequencyKhz: number;
  idleDuration: string;
  maxScalingFrequencyKhz: number;
}

interface CStateValue {
  displayName: string;
  sessionDuration: string;
}

interface CpuStatusReports {
  cpuUtilizationPercentageInfo: number[];
  cpuTemperatureInfo: CpuTemperatureInfo[];
  reportTime: ChromeDeviceDate;
}

interface CpuTemperatureInfo {
  label: string;
  temperature: number;
}

interface DiskVolumeReports {
  volumeInfo: VolumeInfo[];
}

interface VolumeInfo {
  storageFree: number;
  storageTotal: number;
  volumeId: string;
}

interface SystemRamFreeReports {
  reportTime: ChromeDeviceDate;
  systemRamFreeInfo: number[];
}

export interface ChromeDeviceDate {
  value: number;
  dateOnly: boolean;
  tzShift: number;
}

export interface ChromeDeviceModel {
  activeTimeRanges: ActiveTimeRangesModel[] | null;
  annotatedUser: string | null;
  annotatedLocation: string | null;
  annotatedAssetId: string | null;
  autoUpdateExpiration: number | null;
  bootMode: string | null;
  cpuInfo: CpuInfo[] | null;
  cpuStatusReports: CpuStatusReports[] | null;
  deviceId: string;
  deviceLicenseType: string;
  diskVolumeReports: DiskVolumeReports[] | null;
  deviceFiles: DevicesFilesModel[] | null;
  etag: string;
  ethernetMacAddress: string | null;
  firstEnrollmentTime: string | null;
  firmwareVersion: string | null;
  lastEnrollmentTime: ChromeDeviceDate | null;
  lastSync: ChromeDeviceDate | null;
  macAddress: string | null;
  meid: string | null;
  model: string | null;
  orderNumber: string | null;
  orgUnitId: string | null;
  orgUnitPath: string | null;
  osVersion: string | null;
  platformVersion: string | null;
  notes: string | null;
  recentUsers: RecentUsersModel[] | null;
  serialNumber: string | null;
  status: string;
  supportEndDate: number | null;
  systemRamFreeReports: SystemRamFreeReports[] | null;
  systemRamTotal: number | null;
  tpmVersionInfo: TpmVersionInfoModel | null;
  willAutoRenew: boolean | null;
  diskSpaceUsage: DiskSpaceUsageModel | null;
}

export interface DiskSpaceUsageModel {
  capacityBytes: string | null;
  usedBytes: string | null;
}

export interface MoveDevicesToOu {
  devicesToMoveModel: ChromeOsMoveDevicesToOuModel;
  orgUnitPath: string;
}

export interface ChromeOsMoveDevicesToOuModel {
  deviceIds: string[];
}

export interface ChromeDeviceActionModel {
  action: 'deprovision' | 'disable' | 'reenable';
  deprovisionReason: string;
}

export interface Notification {
  id: number;
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'error' | 'success';
  date: number;
  read: boolean;
  docLink: string | null;
}

export interface ActivityModel {
  actor: ActorModel;
  etag: string;
  events: EventModel[];
  id: IdModel;
  ipAddress: string;
  ownerDomain: string;
}

export interface ActorModel {
  callerType: string;
  email: string;
  profileId: string;
  key: string;
}

export interface EventModel {
  name: string;
  parameters: ParameterModel[];
  type: string;
  eventNiceName: string;
  action: string;
}

export interface ParameterModel {
  name: string;
  value: string;
  intValue: number;
  boolValue: boolean;
}

export interface IdModel {
  applicationName: string;
  customerId: string;
  time: DateTimeModel;
  uniqueQualifier: number;
}

export interface DateTimeModel {
  value: number;
  dateOnly: boolean;
  tzShift: number;
}

export interface GmailDeletionModel {
  sourceType: 'User' | 'Group' | 'OrgUnit' | 'Domain';
  sources: string[];
  type: string;
  lastRun: number;
  created: number;
  lastUpdated: number;
  createdBy: string;
  updatedBy: string;
  primaryDomain: string;
  whereToSearch: string;
  from: string;
  to: string;
  subject: string;
  hasTheWords: string;
  doesntHaveTheWords: string;
  converted: boolean;
  searchString: string;
}

export interface GmailAuditModel {
  id: string;
  searchQuery: string;
  user: string;
  beginDate: number;
  endDate: number;
  completedDate: number;
  requestDate: number;
  includeDeleted: boolean;
  headerOnly: boolean;
  status:
    | 'PENDING'
    | 'ERROR'
    | 'COMPLETED'
    | 'MARKED_DELETE'
    | 'DELETED'
    | 'EXPIRED';
  downloadUrls: string[];
}

export interface GmailExportKey {
  date: number;
  user: string;
}

export interface BulkImportModel {
  name: string;
  description: string;
  bulkImport: string;
  spreadsheetId: string | null;
  columns: SpreadSheetColumn[] | null;
  notifyEmails: string[] | null;
  sourceType:
    | 'All_Users'
    | 'All_Groups'
    | 'User'
    | 'Group'
    | 'OrgUnit'
    | 'gPanel_Role'
    | 'Mobile_Device'
    | 'Domain'
    | null;
  sources: string[] | null;
  includeNestedGroups: string[] | null;
  allSource: boolean;
  reportHasRan: boolean;
  vacationResponderData: VacationModel | null;
  createdDate: number;
  id: number;
  lastRan: number;
  lastExportUpdate: number | null;
  createdBy: string;
  path: 'guideMe' | 'advanced' | null;
  setAsProcessed: boolean;
  createGroups: boolean;
  resourceIds: string[] | null;
  startDate: number;
  endDate: number;
  existingLabelFilter?: string | null;
  visibilityFilter?: string | null;
  status: 'IDLE' | 'RUNNING' | 'COMPLETED' | null;
}

export interface BulkImportType {
  name: string;
  niceName: string;
  documentationLink: string;
  youtubeLink: string;
  description: string;
  columns: SpreadSheetColumn[];
  reportType: string;
  role: string;
  usesSpreadsheet: boolean;
  usesFolder: boolean;
  usesSpreadsheetResult: boolean;
}

export interface SpreadSheetLines {
  [key: string]: string[];
}

export interface SpreadsheetLinesResult {
  lines: SpreadSheetLines;
  missingRequiredColumns: string[];
}

export interface ApigeeApp {
  appKey: string;
  name: string;
  description: string;
  developer: string;
  domain: string;
  lastModifiedTime: number;
  loggingId: number;
}

export interface IP_Settings {
  enabled: boolean;
  apiIpRestricting: boolean;
  primaryDomain: string;
  users: string[];
  ipAddresses: string[];
  ipRanges: string[];
  updatedBy: string;
  lastUpdatedTime: number;
}

export interface IpIntrusionRule {
  type: string;
  value: string;
}

export interface FeatureEntity {
  createdDate?: number;
  creator?: string;
  description: string;
  featureName: string;
  updatedBy?: string;
  updatedDate?: number;
  featureString: string;
  hidden: boolean;
}

export type ErrorType = 'error' | 'warning' | 'info';

export type RoleMemberType = 'USER' | 'GROUP';

export interface RoleMember {
  userEmail: string;
  role: string;
  isAppAdmin: boolean;
  expiryTime?: number;
  memberType?: RoleMemberType;
  groupName?: string;
}

export type BulkOperationResponseStatus =
  | 'SUCCESS'
  | 'PARTIAL_FAILURE'
  | 'FAILURE';

export interface BulkOperationResponse {
  status: BulkOperationResponseStatus;
  succeeded: string[];
  failed: string[];
}

export interface DomainDefaultModel {
  lastUpdated: number;
  lastUpdatingUser: string;
  domainDefaultType: string;
  isActive: boolean;
}

export interface ResourceCalendarDefault extends DomainDefaultModel {
  calSharedToDomain: boolean;
  calPublic: boolean;
  domainRole: 'freeBusyReader' | 'reader';
  publicRole: 'freeBusyReader' | 'reader';
  shares?: object;
  buildingId?: string;
  floorName?: string;
  resourceType?: string;
  capacity: number | null;
  floorSection: string;
  resourceCategory: string;
}

export interface SecondaryDomainModel {
  primaryDomain: string;
  domain: string;
  customerId: string;
  createdDate: number;
  performingUser: string;
}

export interface PossibleDomain {
  domain: string;
  status: string;
}

export interface BulkAddSecondaryDomainsResponse {
  [domain: string]: boolean;
}

export interface IndexUserLog {
  emailDomain: string;
  usersAdded: number;
  usersDeleted: number;
  usersUpdated: number;
  lastRunTime: number;
  aliasesAdded: number;
  aliasesDeleted: number;
  manual: boolean;
}

export interface IndexGroupLog {
  emailDomain: string;
  lastRunTime: number;
  manual: boolean;
}

export interface IndexCalendarLog {
  emailDomain: string;
  lastRunTime: number;
  manual: boolean;
}

export interface IndexOrgLog {
  emailDomain: string;
  lastRunTime: number;
  manual: boolean;
}

export interface DecomModel {
  name: string;
  org: string[];
  actions: {
    [key: number]: DecomActionListModel;
  };
  notificationUser: string;
  createdBy: string;
  createdTime: number;
  lastUpdatedBy: string;
  lastUpdatedTime: number;
  keepUnsuspended: boolean;
  monitorSuspended: boolean;
  sendSummary: boolean;
  defaultNotifyUser: boolean;
  defaultUserToNotify: string[];
  defaultUseManagerEmail: boolean | null;
  defaultDestUser: DestUser | null;
  id: string;
  isActive: boolean;
  triggerType: 'suspension' | 'move';
  userId: string;
  actImmediately: boolean;
  validateUserIsStillInDecomOrg: boolean;
  validateUserIsStillSuspended: boolean;
}

export interface DecomActionListModel {
  actions: DecomActionModel[];
  day: number;
}

export interface DecomActionModel {
  actionType: string;
  destUser: DestUser[] | null;
  newOrgPath: string | null;
  notifyUser: boolean;
  userToNotify: string[];
  // EmailDump Specs
  startdate: number;
  starttime: string | null;
  enddate: number;
  endtime: string | null;
  getDeleted: boolean | null;
  query: string | null;
  exportType: 'FULL' | 'HEADERS' | null;
  matterId: string | null;
  corpus: VaultCorpus | null;
  dataScope: 'ALL_DATA' | 'HELD_DATA' | 'UNPROCESSED_DATA' | 'UNKNOWN' | null;
  searchMethod:
    | 'ACCOUNT'
    | 'ORG_UNIT'
    | 'TEAM_DRIVE'
    | 'SHARED_DRIVE'
    | 'SITES_URL'
    | 'ROOM'
    | 'UNKNOWN'
    | null;
  timeZone: string | null;
  terms: string | null;
  exportFormat: 'MBOX' | 'PST' | 'ICS' | 'XML' | 'UNKNOWN' | null;
  vaultStartTime: string | null;
  vaultEndTime: string | null;
  vaultVersionDate: string | null;
  vaultExcludeDrafts: boolean | null;
  vaultIncludeChatSpaces: boolean | null;
  vaultSharedDrivesOption: string | null;
  vaultPeopleQuery: string[] | null;
  vaultLocationQuery: string[] | null;
  vaultMinusWords: string[] | null;
  vaultResponseStatus: string[] | null;
  destFolderName: string | null;
  // TransferDocs Specs
  destFolderId: string | null;
  transferAction:
    | 'editor'
    | 'viewer'
    | 'removeOld'
    | 'removeAll'
    | 'commenter'
    | 'noAction'
    | null;
  // vacation responder
  subject: string | null;
  message: string | null;
  // user name change
  preName: string | null;
  postName: string | null;
  addDate: boolean | null;
  datePosition: 'prefix' | 'suffix' | null;
  mustAcknowledgeLogs: boolean;
  useManagerEmail: boolean | null;
  targetEmailType: string[] | null;
  customEmailType: string[] | null;
  useSharedDrive: boolean | null;
  newPassword: string | null;
  forceResetPassword: boolean | null;
  staticPassword: boolean | null;
  isSendPassword: boolean | null;
  daysUntilExpiration: number | null;
  emailSubject: string | null;
  emailContent: string | null;
  emailSendTo: string[] | null;
  emailCcTo: string[] | null;
  emailBccTo: string[] | null;
  sharingScope: 'all' | 'external' | 'internal' | null;
}

export interface TrialUserModel {
  userEmail: string;
}

export interface DecomSuspendedUserModel {
  keyString: string;
  timeSuspended: number;
  email: string;
  daysout: number;
  actor: string;
  moveon: boolean;
  errored: boolean;
  reason: string;
  readyToDelete: boolean;
  actionByAdmin: boolean;
  isactive: boolean;
  isWaitingForGmail: boolean;
  decomComplete: boolean;
  isWaitingForCalendars: boolean;
  isWaitingForDocs: boolean;
  setCalendarTransferId: string;
  setDocsTransferId: string;
  emailTransferId: string;
  userId: string;
  decomModelKeyString: string;
  completionTime: number;
  creationDate: number;
  isInitiallySuspended: boolean;
  isInitiallyArchived: boolean;
  isTerminated: boolean;
}

export interface DecomLogModel {
  message: string;
  date: number;
  actionType: string;
  daysout: number;
  decomName: string;
  action: string;
  userEmail: string;
  acknowledged: boolean;
  actionModelType: string;
  actionComplete: boolean;
  userId: string;
  creationDate: number;
  errorMessage: string;
  retries: number;
}

export interface EmailQueueModel {
  id?: number;
  primaryDomain: string;
  createdTime: number;
  sendFrom?: string;
  sendFromName?: string;
  replyTo?: string;
  replyToName?: string;
  subject: string;
  body: string;
  recipients: string[];
  ccs: string[];
  bccs: string[];
  numberOfFailedAttempts?: number;
  scheduledSendDate?: number | null;
}

export interface AuthResponse {
  result:
    | 'SUCCESS'
    | 'NO_HEADER'
    | 'IP_RESTRICTED'
    | 'INVALID_ADMIN'
    | 'MESSAGE'
    | 'INVALID_DOMAIN'
    | 'ERROR'
    | 'TRIAL'
    | 'INVALID_SECONDARY_DOMAIN';
  hasNoAdmin: boolean;
  loggedInUserEmail: string;
  oldAdmin: string;
  currentUserIsAdmin: boolean;
  primaryDomain: string;
  messageSubject: string;
  messageBody: string;
  hasUpdatedFirebaseEmail: boolean;
}

export interface MobileSearchFilter {
  email: string;
  status:
    | 'pending'
    | 'approved'
    | 'account_wiped'
    | 'blocked'
    | 'unprovisioned'
    | 'wiped'
    | '';
  type: 'android' | 'ios' | '';
  syncType: 'inclusiveRange' | 'beforeDate' | 'afterDate';
  syncStartDate: string | null;
  syncEndDate: string | null;
}

// https://developers.hubspot.com/docs/reference/api/conversations/chat-widget-sdk
declare global {
  interface Window {
    showHubSpotWidget?: () => void;
    hsConversationsSettings?: {
      loadImmediately: boolean;
    };
    HubSpotConversations?: {
      widget?: {
        load: () => void;
        open: () => void;
        remove: () => void;
        status: () => {
          loaded: boolean;
        };
      };
    };
  }
}

export interface CloneGroupRequest {
  sourceGroupEmail: string;
  sourceGroupId: string;
  newGroupEmail: string;
  newGroupDescription: string;
  newGroupName: string;
  newGroupLabels: { [key: string]: string };
  cloneBusinessSettings: boolean;
  cloneSecuritySettings: boolean;
  cloneMembers: boolean;
  changeLogMessage: string;
}

export interface TemplateGenerationRequest {
  templateName: string;
  columnHeaders?: string[];
  columnNotes?: { [columnName: string]: string }; // Optional dynamic notes for specific columns
  sheetColumnHeaders?: { [sheetName: string]: SheetHeaderColumn[] };
  columnValidation?:
    | { [columnName: string]: string }
    | {
        [columnName: string]: string[];
      };
}

export interface SheetHeaderColumn {
  name: string;
  description?: string;
}

export interface ScheduledUserStatusUpdate {
  scheduledDatetime: number;
  newStatus: string;
  userEmail: string;
  primaryDomain: string;
  creationTime?: number;
  adminEmail?: string;
  retryCount?: number;
}

export interface PersonalPin {
  googleId: string;
  pinnedAt: number;
}

export enum PinAction {
  PIN = 'pin',
  UNPIN = 'unpin',
}

export type SlackConnectionStatus = {
  connected: boolean;
  teamId?: string;
  teamName?: string;
  userId?: string;
  userName?: string;
  scopes?: string[];
  lastUpdatedTime?: number;
  tokenError?: string;
};
